import React, { useState, useMemo } from 'react'
import {
  Chip,
  ChipSize,
  ChipType,
  ChipWrapper,
  DeletableChip,
} from 'components/common/chip'
import { ServiceArea } from 'types/common'
import { useRequestFormOptions } from 'apis/hooks'
import { observer } from 'mobx-react'
import { Column, Row } from 'components/common/layout'
import { SearchInput } from 'components/common/search-input'
import styled from 'styled-components'
import { theme } from 'infra/theme'
import { FiCheck, FiChevronRight } from 'react-icons/fi'
import { BtnSize, Button } from 'components/common/button'
import { getSelectedChildrenByParent } from 'utils/formatters'
import { MediumText, SmallText } from 'components/common/text'
import { CHANNEL_TALK_URL } from 'infra/constants'

const BusinessModelChips: React.FC<{
  serviceAreas: ServiceArea[]
  selectedServiceAreas: ServiceArea[]
  businessModel: ServiceArea | undefined
  toggleServiceArea: (item: ServiceArea) => void
  checkServiceAreaLimitExceeded?: () => boolean
  chipSize?: ChipSize
}> = ({
  serviceAreas,
  selectedServiceAreas,
  businessModel,
  toggleServiceArea,
  checkServiceAreaLimitExceeded,
  chipSize,
}) => {
  const childBusinessModels = serviceAreas.filter(
    (item) => item.parentId === businessModel?.id,
  )
  const isSelectedAllChildBusinessModels =
    childBusinessModels?.length ===
    selectedServiceAreas.filter((item) => item.parentId === businessModel?.id)
      .length
  const selectedServiceAreaIds = selectedServiceAreas.map((item) => item.id)
  return (
    <ChipWrapper>
      <Chip
        size={chipSize}
        mobileSize={chipSize}
        isSelected={isSelectedAllChildBusinessModels}
        onClick={() => {
          if (isSelectedAllChildBusinessModels) {
            // 이미 전체 선택 클릭된 경우, 전체 선택 해제
            childBusinessModels.forEach((childItem) => {
              toggleServiceArea(childItem)
            })
          } else {
            // 이미 선택된 항목 제외하고 전체 선택
            const toggledServiceAreas: ServiceArea[] = []
            childBusinessModels?.forEach((childItem) => {
              if (!selectedServiceAreaIds.includes(childItem.id)) {
                toggleServiceArea(childItem)
                toggledServiceAreas.push(childItem)
              }
            })
            if (
              checkServiceAreaLimitExceeded &&
              checkServiceAreaLimitExceeded()
            ) {
              // 선택된 서비스 분야가 10개를 초과하는 경우, 전체 선택 해제
              toggledServiceAreas.forEach((childItem) => {
                toggleServiceArea(childItem)
              })
              alert(
                '비즈니스 모델과 서비스 분야를 합쳐 10개까지 선택할 수 있어요',
              )
            }
          }
        }}
      >
        전체
      </Chip>
      {childBusinessModels?.map((item) => (
        <Chip
          key={item.id}
          size={chipSize}
          mobileSize={chipSize}
          isSelected={selectedServiceAreaIds.includes(item.id)}
          onClick={() => {
            toggleServiceArea(item)
            if (
              checkServiceAreaLimitExceeded &&
              checkServiceAreaLimitExceeded()
            ) {
              toggleServiceArea(item)
              alert(
                '비즈니스 모델과 서비스 분야를 합쳐 10개까지 선택할 수 있어요',
              )
            }
          }}
        >
          {item.name}
        </Chip>
      ))}
    </ChipWrapper>
  )
}

const ServiceSearch: React.FC<{
  serviceAreas: ServiceArea[]
  selectedServiceAreaIds: number[]
  parentServiceAreas: ServiceArea[]
  businessModel: ServiceArea | undefined
  toggleServiceArea: (item: ServiceArea) => void
  checkServiceAreaLimitExceeded?: () => boolean
  chipSize?: ChipSize
}> = ({
  serviceAreas,
  selectedServiceAreaIds,
  parentServiceAreas,
  businessModel,
  toggleServiceArea,
  checkServiceAreaLimitExceeded,
  chipSize,
}) => {
  const [searchKeyword, setSearchKeyword] = useState('')

  const searchResultServiceAreas = searchKeyword.trim()
    ? [
        ...new Set( // 중복 제거
          serviceAreas
            .filter(
              (item) =>
                item.name.toUpperCase().includes(searchKeyword.toUpperCase()) &&
                item.parentId !== businessModel?.id &&
                item.id !== businessModel?.id, // 비즈니스 모델 제외
            )
            .flatMap((item) => {
              if (item.parentId !== null) {
                // parentId가 null이 아니면 그대로 유지
                return [item]
              } else {
                // parentId가 null이면 해당 요소는 제거하고, 자식들만 반환
                const children = serviceAreas.filter(
                  (child) => child.parentId === item.id,
                )
                return children
              }
            }),
        ),
      ]
    : []

  const highlightSearchQuery = (text: string, searchKeyword: string) => {
    if (!searchKeyword) return text
    const parts = text.split(new RegExp(`(${searchKeyword})`, 'gi'))

    return (
      <div>
        {parts.map((part, idx) =>
          part.toLowerCase() === searchKeyword.toLowerCase() ? (
            <span key={idx} style={{ color: theme.color.primary }}>
              {part}
            </span>
          ) : (
            part
          ),
        )}
      </div>
    )
  }

  return (
    <SearchWrapper
      style={{
        fontSize: chipSize === ChipSize.EXTRA_SMALL ? 14 : 15,
      }}
    >
      <SearchInput
        value={searchKeyword}
        placeholder='찾으시는 서비스 분야를 입력해주세요'
        onChange={(e) => setSearchKeyword(e.target.value)}
      />
      {searchKeyword && (
        <SearchResultWrapper>
          {searchResultServiceAreas?.length === 0 ? (
            <SearchResultItem
              style={{
                backgroundColor: theme.color.white1,
                cursor: 'auto',
              }}
            >
              <RequestServiceAreaWrapper>
                <SmallText>찾으시는 서비스 분야가 없으신가요?</SmallText>
                <LinkText
                  onClick={() => (window.location.href = CHANNEL_TALK_URL)}
                >
                  서비스 분야 등록 요청하기
                </LinkText>
              </RequestServiceAreaWrapper>
            </SearchResultItem>
          ) : (
            searchResultServiceAreas?.map((item) => (
              <SearchResultItem
                key={item.id}
                onClick={() => {
                  toggleServiceArea(item)
                  if (
                    checkServiceAreaLimitExceeded &&
                    checkServiceAreaLimitExceeded()
                  ) {
                    toggleServiceArea(item)
                    alert(
                      '비즈니스 모델과 서비스 분야를 합쳐 10개까지 선택할 수 있어요',
                    )
                  }
                }}
              >
                <Row style={{ gap: 8 }}>
                  <CheckboxIconWrapper
                    isSelected={selectedServiceAreaIds.includes(item.id)}
                    size={chipSize}
                  >
                    <FiCheck
                      size={
                        chipSize === ChipSize.EXTRA_SMALL ||
                        chipSize === ChipSize.XX_SMALL
                          ? 16
                          : 20
                      }
                      color={
                        selectedServiceAreaIds.includes(item.id)
                          ? theme.color.white1
                          : theme.color.gray3
                      }
                    />
                  </CheckboxIconWrapper>
                  <Row style={{ gap: 2 }}>
                    {highlightSearchQuery(
                      parentServiceAreas.find(
                        (parent) => parent.id === item.parentId,
                      )?.name || '',
                      searchKeyword,
                    )}
                    <FiChevronRight />
                    {highlightSearchQuery(item.name, searchKeyword)}
                  </Row>
                </Row>
              </SearchResultItem>
            ))
          )}
          <SearchResultFooter>
            <Button
              size={BtnSize.SMALL}
              onClick={() => {
                setSearchKeyword('')
              }}
              style={{ width: 80 }}
            >
              닫기
            </Button>
          </SearchResultFooter>
        </SearchResultWrapper>
      )}
    </SearchWrapper>
  )
}

const ServiceList: React.FC<{
  serviceAreas: ServiceArea[]
  parentServiceAreas: ServiceArea[]
  selectedServiceAreaIds: number[]
  toggleServiceArea: (item: ServiceArea) => void
  checkServiceAreaLimitExceeded?: () => boolean
  chipSize?: ChipSize
}> = ({
  serviceAreas,
  parentServiceAreas,
  selectedServiceAreaIds,
  toggleServiceArea,
  checkServiceAreaLimitExceeded,
  chipSize,
}) => {
  const [selectedParentServiceArea, setSelectedParentServiceArea] =
    useState<ServiceArea | null>(null)

  const childServiceAreas =
    (selectedParentServiceArea &&
      serviceAreas.filter(
        (item) => selectedParentServiceArea.id === item.parentId,
      )) ||
    []

  return (
    <Container
      style={{
        fontSize: chipSize === ChipSize.EXTRA_SMALL ? 14 : 15,
      }}
    >
      <LeftPanel>
        {parentServiceAreas.map((item) => (
          <CategoryItem
            key={item.id}
            selected={selectedParentServiceArea?.id === item.id}
            onClick={() => setSelectedParentServiceArea(item)}
          >
            {item.name}
          </CategoryItem>
        ))}
      </LeftPanel>
      <RightPanel>
        {childServiceAreas.length > 0 ? (
          <>
            <SubCategoryItem
              selected={childServiceAreas.every((item) =>
                selectedServiceAreaIds.includes(item.id),
              )}
              onClick={() => {
                const isAllSelected = childServiceAreas.every((item) =>
                  selectedServiceAreaIds.includes(item.id),
                )
                const toggledServiceAreas: ServiceArea[] = []
                childServiceAreas.forEach((item) => {
                  if (isAllSelected) {
                    toggleServiceArea(item)
                  } else if (!selectedServiceAreaIds.includes(item.id)) {
                    toggleServiceArea(item)
                    toggledServiceAreas.push(item)
                  }
                })
                if (
                  checkServiceAreaLimitExceeded &&
                  checkServiceAreaLimitExceeded()
                ) {
                  // 선택된 서비스 분야가 10개를 초과하는 경우, 전체 선택 해제
                  toggledServiceAreas.forEach((item) => {
                    toggleServiceArea(item)
                  })
                  alert(
                    '비즈니스 모델과 서비스 분야를 합쳐 10개까지 선택할 수 있어요',
                  )
                }
              }}
            >
              <Row style={{ gap: 8 }}>
                <CheckboxIconWrapper
                  isSelected={childServiceAreas.every((item) =>
                    selectedServiceAreaIds.includes(item.id),
                  )}
                  size={chipSize}
                >
                  <FiCheck
                    size={
                      chipSize === ChipSize.EXTRA_SMALL ||
                      chipSize === ChipSize.XX_SMALL
                        ? 16
                        : 20
                    }
                    color={
                      childServiceAreas.every((item) =>
                        selectedServiceAreaIds.includes(item.id),
                      )
                        ? theme.color.white1
                        : theme.color.gray3
                    }
                  />
                </CheckboxIconWrapper>
                전체
              </Row>
            </SubCategoryItem>
            {childServiceAreas.map((item) => (
              <SubCategoryItem
                key={item.id}
                selected={selectedServiceAreaIds.includes(item.id)}
                onClick={() => {
                  toggleServiceArea(item)
                  if (
                    checkServiceAreaLimitExceeded &&
                    checkServiceAreaLimitExceeded()
                  ) {
                    toggleServiceArea(item)
                    alert(
                      '비즈니스 모델과 서비스 분야를 합쳐 10개까지 선택할 수 있어요',
                    )
                  }
                }}
              >
                <Row style={{ gap: 8 }}>
                  <CheckboxIconWrapper
                    isSelected={selectedServiceAreaIds.includes(item.id)}
                    size={chipSize}
                  >
                    <FiCheck
                      size={
                        chipSize === ChipSize.EXTRA_SMALL ||
                        chipSize === ChipSize.XX_SMALL
                          ? 16
                          : 20
                      }
                      color={
                        selectedServiceAreaIds.includes(item.id)
                          ? theme.color.white1
                          : theme.color.gray3
                      }
                    />
                  </CheckboxIconWrapper>
                  {item.name}
                </Row>
              </SubCategoryItem>
            ))}
          </>
        ) : (
          <EmptyMessageWrapper>
            <SubTitleText>대분류를 먼저 선택해주세요</SubTitleText>
          </EmptyMessageWrapper>
        )}
      </RightPanel>
    </Container>
  )
}

const SelectedServices: React.FC<{
  serviceAreas: ServiceArea[]
  selectedServiceAreas: ServiceArea[]
  parentServiceAreas: ServiceArea[]
  toggleServiceArea: (item: ServiceArea) => void
  businessModel: ServiceArea | undefined
}> = ({
  serviceAreas,
  selectedServiceAreas,
  parentServiceAreas,
  toggleServiceArea,
  businessModel,
}) => {
  const selectedChildrenByParent = useMemo(
    () =>
      getSelectedChildrenByParent(
        serviceAreas || [],
        selectedServiceAreas || [],
      ),
    [serviceAreas, selectedServiceAreas],
  )
  return (
    <ChipWrapper>
      {selectedChildrenByParent.map((parent) => {
        if (parent.selectedChildren.length === 0) return
        if (
          parent.total === parent.selectedChildren.length &&
          parent.id !== businessModel?.id
        )
          return (
            <CustomDeletableChip
              key={parent.id}
              onClick={() =>
                selectedServiceAreas
                  .filter((item) => item.parentId === parent.id)
                  .forEach((item) => toggleServiceArea(item))
              }
            >
              {parent.name} 전체
            </CustomDeletableChip>
          )
        return parent.selectedChildren
          .filter((child) => child.parentId !== businessModel?.id)
          .map((child) => {
            return (
              <CustomDeletableChip
                key={child.id}
                onClick={() => toggleServiceArea(child as ServiceArea)}
              >
                <Row>
                  {
                    parentServiceAreas.find(
                      (parent) => parent.id === child.parentId,
                    )?.name
                  }
                  <FiChevronRight />
                  {child.name}
                </Row>
              </CustomDeletableChip>
            )
          })
      })}
    </ChipWrapper>
  )
}

export const ServiceSelector: React.FC<{
  selectedServiceAreas: ServiceArea[]
  toggleServiceArea: (item: ServiceArea) => void
  checkServiceAreaLimitExceeded?: () => boolean
  chipSize?: ChipSize
}> = observer(
  ({
    selectedServiceAreas,
    toggleServiceArea,
    checkServiceAreaLimitExceeded,
    chipSize,
  }) => {
    const { data: options } = useRequestFormOptions()
    if (!options) return null

    // 비즈니스 모델은 따로 선택하기 위해 분리
    const businessModel = options.serviceAreas.find(
      (item) => item.name === '비즈니스 모델',
    )

    const parentServiceAreas =
      options.serviceAreas.filter(
        (item) => item.parentId === null && item.id != businessModel?.id,
      ) || []

    const selectedServiceAreaIds = selectedServiceAreas.map((item) => item.id)

    return (
      <Column style={{ gap: 24 }}>
        <Column style={{ gap: 12 }}>
          <SubTitleText>비즈니스 모델</SubTitleText>
          <BusinessModelChips
            serviceAreas={options.serviceAreas}
            selectedServiceAreas={selectedServiceAreas}
            businessModel={businessModel}
            toggleServiceArea={toggleServiceArea}
            checkServiceAreaLimitExceeded={checkServiceAreaLimitExceeded}
            chipSize={chipSize}
          />
        </Column>
        <Column style={{ gap: 12 }}>
          <SubTitleText>서비스 분야</SubTitleText>
          <ServiceSearch
            serviceAreas={options.serviceAreas}
            selectedServiceAreaIds={selectedServiceAreaIds}
            parentServiceAreas={parentServiceAreas}
            businessModel={businessModel}
            toggleServiceArea={toggleServiceArea}
            checkServiceAreaLimitExceeded={checkServiceAreaLimitExceeded}
            chipSize={chipSize}
          />
          <ServiceList
            serviceAreas={options.serviceAreas}
            parentServiceAreas={parentServiceAreas}
            selectedServiceAreaIds={selectedServiceAreaIds}
            toggleServiceArea={toggleServiceArea}
            checkServiceAreaLimitExceeded={checkServiceAreaLimitExceeded}
            chipSize={chipSize}
          />
          <SelectedServices
            serviceAreas={options.serviceAreas}
            selectedServiceAreas={selectedServiceAreas}
            parentServiceAreas={parentServiceAreas}
            toggleServiceArea={toggleServiceArea}
            businessModel={businessModel}
          />
        </Column>
      </Column>
    )
  },
)

const SubTitleText = styled(MediumText)`
  color: ${(p) => p.theme.color.gray1};
  font-size: ${(p) => p.theme.fontSize.small};
`

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
`

const SearchResultWrapper = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background-color: ${(p) => p.theme.color.white1};
  border: 1px solid ${(p) => p.theme.color.line};
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-sizing: border-box;
`

const SearchResultItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  &:hover {
    background-color: ${(p) => p.theme.color.gray5};
  }
`

const SearchResultFooter = styled.div`
  position: sticky;
  bottom: 0;
  background-color: ${(p) => p.theme.color.white1};
  padding: 8px 16px;
  border-top: 1px solid ${(p) => p.theme.color.line};
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const CheckboxIconWrapper = styled(Row)<{
  isSelected: boolean
  size?: ChipSize
}>`
  flex: none;
  width: ${(p) =>
    p.size === ChipSize.EXTRA_SMALL || p.size === ChipSize.XX_SMALL
      ? 14
      : 18}px;
  height: ${(p) =>
    p.size === ChipSize.EXTRA_SMALL || p.size === ChipSize.XX_SMALL
      ? 14
      : 18}px;
  border-radius: 50%;
  border: 1px solid
    ${(p) => (p.isSelected ? p.theme.color.primary : p.theme.color.gray3)};
  background-color: ${(p) =>
    p.isSelected ? p.theme.color.primary : p.theme.color.white1};
`

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 300px;
  border: 1px solid ${(p) => p.theme.color.line};
  border-radius: 8px;
  overflow: hidden;
  background-color: ${(p) => p.theme.color.white1};
`

const LeftPanel = styled.div`
  width: 40%;
  border-right: 1px solid ${(p) => p.theme.color.line};
  overflow-y: auto;
`

const RightPanel = styled.div`
  width: 60%;
  overflow-y: auto;
  position: relative;
`

const EmptyMessageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.color.white1};
`

const CategoryItem = styled.div<{ selected: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  font-weight: ${(p) => (p.selected ? theme.fontWeight.bold : 'normal')};
  &:hover {
    background-color: ${(p) => p.theme.color.gray5}42;
  }
`

const SubCategoryItem = styled.div<{ selected: boolean }>`
  padding: 10px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:hover {
    background-color: ${(p) => p.theme.color.gray5}42;
  }
`

const CustomDeletableChip = styled(DeletableChip).attrs({
  isSelected: true,
  size: ChipSize.EXTRA_SMALL,
  mobileSize: ChipSize.EXTRA_SMALL,
  variant: ChipType.SUPER_ROW,
})``

const RequestServiceAreaWrapper = styled(Column)`
  gap: 8px;
  align-items: center;
  span {
    color: ${(p) => p.theme.color.secondary};
  }
`

const LinkText = styled(SmallText)`
  font-weight: ${(p) => p.theme.fontWeight.bold};
  text-decoration: underline;
  cursor: pointer;
`
